import { Link } from "react-router-dom";
import {
  Briefcase, HeartHandshake, ArrowRight, Phone, MapPin, Clock3, Award, ShieldCheck,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyCall from "@/components/StickyCall";
import Seo, { LOCAL_BUSINESS_SCHEMA } from "@/components/Seo";
import { COMPANY } from "@/lib/data";

const JOBS = [
  {
    slug: "driver",
    href: "/careers/driver",
    icon: Briefcase,
    title: "NEMT Driver",
    subtitle: "Non-Emergency Medical Transportation",
    accent: "teal",
    blurb:
      "Drive ADA-compliant wheelchair vans transporting patients to medical appointments across Virginia, DC, and Maryland.",
    highlights: [
      "Valid driver's license required (21+)",
      "Paid wheelchair-securement training",
      "Full-time, part-time, and PRN shifts",
    ],
    tags: ["Full-Time", "Part-Time", "PRN"],
    ctaTestId: "careers-cta-driver",
  },
  {
    slug: "monitor",
    href: "/careers/transportation-monitor",
    icon: HeartHandshake,
    title: "Transportation Monitor",
    subtitle: "Passenger Attendant · No driving required",
    accent: "navy",
    blurb:
      "Ride along on student and medical transportation routes to help supervise and assist passengers — keeping every trip safe, calm, and comfortable.",
    highlights: [
      "No driver's license required",
      "Comfortable with students, seniors, or riders needing extra support",
      "Flexible morning &amp; afternoon shifts",
    ],
    tags: ["Flexible Hours", "Paid Training", "Meaningful Work"],
    ctaTestId: "careers-cta-monitor",
  },
];

export default function Careers() {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Careers — Now Hiring in Virginia, DC & Maryland | Comfort Care Transportation"
        description="We're hiring compassionate, dependable people across Virginia, DC, and Maryland. Explore open positions at Comfort Care Transportation — NEMT Drivers and Transportation Monitors / Passenger Attendants."
        path="/careers"
        keywords={[
          "NEMT jobs Virginia",
          "transportation jobs DC Maryland",
          "passenger attendant jobs",
          "school bus monitor Virginia",
          "medical transportation careers",
          "Comfort Care careers",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [LOCAL_BUSINESS_SCHEMA],
        }}
      />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="grain-bg py-16 md:py-24">
          <div className="container-pad mx-auto max-w-7xl">
            <Link to="/" className="text-sm text-navy/60 hover:text-teal-600">← Back to Home</Link>
            <div className="grid lg:grid-cols-12 gap-12 mt-6 items-center">
              <div className="lg:col-span-7">
                <span className="eyebrow">
                  <Briefcase className="w-3.5 h-3.5" /> Careers · Now Hiring
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-navy tracking-tight mt-5 leading-[1.05]">
                  Do work that <strong className="font-bold text-teal-600">matters.</strong>
                </h1>
                <p className="mt-6 text-lg text-navy/70 leading-relaxed max-w-2xl font-light">
                  Comfort Care Transportation is growing across Virginia, Washington DC, and Maryland.
                  We&apos;re hiring reliable, compassionate people for two distinct roles — choose the one
                  that fits you best and apply in minutes.
                </p>
                <div className="flex flex-wrap gap-3 mt-8">
                  <a href="#positions" className="btn-primary" data-testid="see-openings-cta">
                    See open positions <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="btn-outline">
                    <Phone className="w-4 h-4" /> Call {COMPANY.phone}
                  </a>
                </div>
                <div className="mt-8 flex flex-wrap gap-4 text-sm text-navy/70">
                  <PerkChip icon={MapPin} label="VA · DC · MD" />
                  <PerkChip icon={Clock3} label="Flexible hours" />
                  <PerkChip icon={Award} label="Paid training" />
                  <PerkChip icon={ShieldCheck} label="Safety-first culture" />
                </div>
              </div>
              <div className="lg:col-span-5">
                <img
                  src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/l5bnk78a_2C3E0AE7-BC6B-41D5-8937-5C064B8E8692.PNG"
                  alt="Comfort Care Transportation branded wheelchair van"
                  className="rounded-3xl w-full h-[420px] object-cover shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Open positions */}
        <section id="positions" className="section-pad">
          <div className="container-pad mx-auto max-w-7xl">
            <div className="max-w-3xl mb-12">
              <span className="eyebrow">
                <span className="w-8 h-px bg-teal-500" />
                Open Positions · 2 roles
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
                Two ways to <strong className="font-bold text-teal-600">join the team.</strong>
              </h2>
              <p className="mt-4 text-navy/70">
                Each role has its own separate application so you can apply to the position that fits you best.
                Not sure which one? <a href={`tel:${COMPANY.phoneRaw}`} className="text-teal-600 font-semibold hover:underline">Give us a call.</a>
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {JOBS.map((job) => (
                <JobCard key={job.slug} job={job} />
              ))}
            </div>
          </div>
        </section>

        {/* Culture strip */}
        <section className="section-pad bg-sand">
          <div className="container-pad mx-auto max-w-5xl text-center">
            <span className="eyebrow justify-center">
              <span className="w-8 h-px bg-teal-500" />
              Our Team
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
              A place where <strong className="font-bold text-teal-600">good work is felt.</strong>
            </h2>
            <p className="mt-5 text-navy/70 max-w-2xl mx-auto leading-relaxed">
              Whether you&apos;re behind the wheel or riding alongside as a monitor, every shift you work directly
              helps a patient, a student, or a senior get where they need to go safely. We hire for character
              first — dependable, patient, kind — and train everything else.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8 text-sm text-navy/70">
              <PerkChip icon={Award} label="Competitive pay" />
              <PerkChip icon={Clock3} label="Flexible scheduling" />
              <PerkChip icon={ShieldCheck} label="Modern, safe vehicles" />
              <PerkChip icon={HeartHandshake} label="Supportive team" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}

function JobCard({ job }) {
  const Icon = job.icon;
  const accentClasses =
    job.accent === "teal"
      ? "from-teal-500/10 to-teal-500/0 text-teal-600 border-teal-100"
      : "from-navy/5 to-navy/0 text-navy border-navy/10";

  return (
    <Link
      to={job.href}
      data-testid={job.ctaTestId}
      className="group relative bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-[0_20px_50px_-25px_rgba(10,25,47,0.15)] hover:shadow-[0_30px_80px_-30px_rgba(0,139,155,0.35)] transition-all duration-300"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${accentClasses.split(" ").filter(c => c.startsWith("from-") || c.startsWith("to-")).join(" ")} opacity-60 pointer-events-none`} />
      <div className="relative p-8 md:p-10">
        <div className={`w-14 h-14 rounded-2xl bg-white border ${accentClasses.split(" ").filter(c => c.startsWith("border-")).join(" ")} flex items-center justify-center ${accentClasses.split(" ").filter(c => c.startsWith("text-")).join(" ")}`}>
          <Icon className="w-7 h-7" strokeWidth={1.5} />
        </div>
        <div className="mt-6 flex flex-wrap items-baseline gap-x-3">
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-navy leading-tight">{job.title}</h3>
        </div>
        <p className="text-xs uppercase tracking-widest text-teal-600 font-bold mt-1">{job.subtitle}</p>
        <p className="mt-4 text-navy/70 leading-relaxed">{job.blurb}</p>

        <ul className="mt-6 space-y-2">
          {job.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 text-sm text-navy/80">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: h }} />
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-wrap gap-2">
          {job.tags.map((t) => (
            <span key={t} className="text-xs font-semibold text-navy/70 bg-sand border border-gray-100 rounded-full px-3 py-1">
              {t}
            </span>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <span className="text-sm font-semibold text-navy/60">
            {job.slug === "driver" ? "5-min application" : "3-min application"}
          </span>
          <span className="inline-flex items-center gap-1.5 text-teal-600 font-semibold group-hover:gap-3 transition-all">
            Apply now <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function PerkChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-gray-200 rounded-full px-4 py-1.5 text-xs font-semibold text-navy">
      <Icon className="w-3.5 h-3.5 text-teal-500" strokeWidth={1.75} />
      {label}
    </span>
  );
}
